import { Fragment } from 'react'
import type { Series, Unit } from '@pharmacia/core'
import { everyNth, exact, niceScale, tick } from './format.ts'
import { box, linePath, Tooltip, useHover } from './plot-core.tsx'
import { colorFor, SOLO } from './seriesColor.ts'

/** The last point a series actually has, which may not be the last slot. */
const lastValue = (data: (number | null)[]): { i: number; v: number } | null => {
  for (let i = data.length - 1; i >= 0; i--) {
    const v = data[i]
    if (v !== null && v !== undefined) return { i, v }
  }
  return null
}

/**
 * Change over time, on ONE axis.
 *
 * Several measures of different scale never share this chart -- that is
 * the small-multiples form instead. A second y-axis is the single most
 * common way a chart lies: two lines cross wherever the author chose to
 * put the scales, and the reader sees a relationship that was invented by
 * the axis.
 */
export function LinePlot({ id, categories, series, unit, width, height = 220 }: {
  id: string
  categories: string[]
  series: Series[]
  unit: Unit
  width: number
  height?: number
}) {
  const values = series.flatMap(s => s.data).filter((v): v is number => v !== null)
  const { max, ticks } = niceScale(Math.max(...values, 0))
  const labels = ticks.map(t => tick(t, unit))
  const gutter = Math.max(34, Math.max(...labels.map(l => l.length)) * 6.4 + 10)

  const b = box(width, height, gutter, 26)
  const n = categories.length
  const x = (i: number) => (n === 1 ? b.left + b.iw / 2 : b.left + (i / (n - 1)) * b.iw)
  const y = (v: number) => b.top + b.ih - (v / max) * b.ih

  const { tip, at, show, clear } = useHover()
  const step = everyNth(n, Math.max(2, Math.floor(b.iw / 56)))
  // Markers are only drawn when they would be readable; on ninety points
  // they merge into a caterpillar and hide the line they are meant to
  // mark. The hover layer still reports every point either way.
  const marks = n <= 24

  const color = (s: Series, i: number) => (series.length === 1 ? SOLO : colorFor(id, s.key ?? String(i)))

  return (
    <>
      <svg
        className="viz-svg" width={width} height={height}
        role="img" aria-label={`${series.map(s => s.label).join(', ')} over time`}
        onMouseLeave={clear}
      >
        {ticks.map(t => (
          <Fragment key={t}>
            <line className="viz-gridline" x1={b.left} x2={b.left + b.iw} y1={y(t)} y2={y(t)} />
            <text className="viz-tick" x={b.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
              {tick(t, unit)}
            </text>
          </Fragment>
        ))}
        <line className="viz-baseline" x1={b.left} x2={b.left + b.iw} y1={y(0)} y2={y(0)} />

        {categories.map((c, i) => (i % step === 0 ? (
          <text key={i} className="viz-cat" x={x(i)} y={height - 8} textAnchor="middle">{c}</text>
        ) : null))}

        {at !== null && (
          <line className="viz-crosshair" x1={x(at)} x2={x(at)} y1={b.top} y2={b.top + b.ih} />
        )}

        {series.map((s, si) => (
          <path
            key={s.key} className="viz-line" stroke={color(s, si)}
            d={linePath(s.data.map((v, i) => (v === null ? null : { x: x(i), y: y(v) })))}
          />
        ))}

        {series.map((s, si) => s.data.map((v, i) => (
          v === null || (!marks && at !== i) ? null : (
            <circle
              key={`${s.key}-${i}`} className="viz-dot"
              cx={x(i)} cy={y(v)} r={at === i ? 5 : 4} fill={color(s, si)}
            />
          )
        )))}

        {/* A direct label at the end of each line, so identity does not
            depend on matching a colour to a legend. Only where the lines
            are few enough that the labels cannot collide; never a number
            on every point. */}
        {series.length <= 4 && series.map((s, si) => {
          const last = lastValue(s.data)
          if (last === null) return null
          return (
            <text
              key={`end-${s.key}`} className="viz-label"
              x={x(last.i)} y={y(last.v) - 9} textAnchor={last.i === n - 1 ? 'end' : 'middle'}
            >
              {exact(last.v, s.unit ?? unit)}
            </text>
          )
        })}

        {/* One invisible column per point, wide enough to be easy to hit. */}
        {categories.map((c, i) => (
          <rect
            key={`hit-${i}`} className="viz-hit"
            x={x(i) - (n > 1 ? b.iw / (n - 1) / 2 : b.iw / 2)} y={b.top}
            width={n > 1 ? b.iw / (n - 1) : b.iw} height={b.ih}
            onMouseMove={e => show(i, {
              title: c,
              rows: series.map((s, si) => ({
                color: color(s, si), label: s.label, value: exact(s.data[i] ?? null, s.unit ?? unit),
              })),
            }, e)}
          />
        ))}
      </svg>
      <Tooltip tip={tip} />
    </>
  )
}
